import React from 'react';
import { SubcontractorExceptionRow, SubcontractorOverview } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';
import { formatDateOnly } from '@utils/formatDate';
import { Progress } from '@components/atoms/Progress';
import { formatMoney, t } from '../utils/format';
import { HealthPill } from './HealthPill';

interface OverviewTabProps {
  overview: SubcontractorOverview;
  onOpenTask: (taskId: string) => void;
}

const ExceptionGroup: React.FC<{
  title: string;
  rows: SubcontractorExceptionRow[];
  onOpenTask: (taskId: string) => void;
  renderMeta: (row: SubcontractorExceptionRow) => string;
}> = ({ title, rows, onOpenTask, renderMeta }) => {
  const [open, setOpen] = React.useState(rows.length > 0);

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-2 text-left text-sm font-semibold text-gray-800 dark:text-gray-100">
        <span>{title}</span>
        <span className="text-gray-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && rows.length > 0 && (
        <ul className="pb-2">
          {rows.map((row) => (
            <li key={`${row.taskId}-${row.incidentId || ''}`}>
              <button
                type="button"
                onClick={() => onOpenTask(row.taskId)}
                className="w-full flex items-center justify-between py-1.5 text-sm text-left text-gray-700 dark:text-gray-300 hover:text-indigo-700 dark:hover:text-indigo-300">
                <span className="truncate">
                  {row.taskName} · {row.projectName}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                  {renderMeta(row)} →
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const OverviewTab: React.FC<OverviewTabProps> = ({ overview, onOpenTask }) => {
  const tSub = useTranslation('subcontractors');
  const { exceptions, projects, materialsEquipmentRollup } = overview;

  const totalAgreed = projects.reduce((sum, p) => sum + p.agreed, 0);
  const totalEarned = projects.reduce((sum, p) => sum + p.earned, 0);
  const totalRemaining = projects.reduce((sum, p) => sum + p.remaining, 0);
  const totalAtRisk = projects.reduce((sum, p) => sum + p.atRisk, 0);

  const allClear = exceptions.awaitingApproval.length + exceptions.openIncidents.length + exceptions.overdue.length === 0;

  return (
    <div className="space-y-8 pb-12">
      <section>
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          {allClear ? tSub?.allClear || 'All clear' : tSub?.needsAttention || 'Needs attention'}
        </h2>
        {allClear ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">✓ {tSub?.allClear || 'All clear'}</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg ring-1 ring-gray-900/5 px-4">
            <ExceptionGroup
              title={t(tSub?.awaitingApproval, 'Awaiting my approval ({n})', {
                n: exceptions.awaitingApproval.length,
              })}
              rows={exceptions.awaitingApproval}
              onOpenTask={onOpenTask}
              renderMeta={(row) => row.reason || ''}
            />
            <ExceptionGroup
              title={t(tSub?.openIncidents, 'Open incidents on their work ({n})', {
                n: exceptions.openIncidents.length,
              })}
              rows={exceptions.openIncidents}
              onOpenTask={onOpenTask}
              renderMeta={(row) => (row.date ? formatDateOnly(row.date) : '')}
            />
            <ExceptionGroup
              title={t(tSub?.overdueGroup, 'Overdue ({n})', { n: exceptions.overdue.length })}
              rows={exceptions.overdue}
              onOpenTask={onOpenTask}
              renderMeta={(row) => (row.date ? formatDateOnly(row.date) : '')}
            />
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          {tSub?.progressByProject || 'Progress by project'}
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg ring-1 ring-gray-900/5 divide-y divide-gray-100 dark:divide-gray-700">
          {projects.map((project) => (
            <div key={project.projectId} className="px-4 py-3 flex items-center gap-4">
              <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                {project.projectName}
              </span>
              <div className="w-40 flex-shrink-0">
                <Progress value={project.completion * 100} size="sm" />
              </div>
              <span className="w-12 text-right text-xs text-gray-500 dark:text-gray-400">
                {Math.round(project.completion * 100)}%
              </span>
              <HealthPill health={project.health} />
              {project.dueThisWeekCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {t(tSub?.dueThisWeek, '{n} due this week', { n: project.dueThisWeekCount })}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          {tSub?.spendVsAgreed || 'Spend vs agreed'}
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg ring-1 ring-gray-900/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2 text-right">{tSub?.colAgreed || 'Agreed'}</th>
                <th className="px-4 py-2 text-right">{tSub?.colEarned || 'Earned'}</th>
                <th className="px-4 py-2 text-right">{tSub?.colRemaining || 'Remaining'}</th>
                <th className="px-4 py-2 text-right">{tSub?.colAtRisk || 'At risk'}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.projectId} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-2 text-gray-800 dark:text-gray-100">{project.projectName}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(project.agreed, tSub?.notSet || 'Not set')}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(project.earned, tSub?.notSet || 'Not set')}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(project.remaining, tSub?.notSet || 'Not set')}</td>
                  <td className="px-4 py-2 text-right">
                    {project.atRisk > 0 ? formatMoney(project.atRisk) : '—'}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-gray-200 dark:border-gray-600 font-semibold">
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2 text-right">{formatMoney(totalAgreed)}</td>
                <td className="px-4 py-2 text-right">{formatMoney(totalEarned)}</td>
                <td className="px-4 py-2 text-right">{formatMoney(totalRemaining)}</td>
                <td className="px-4 py-2 text-right">{totalAtRisk > 0 ? formatMoney(totalAtRisk) : '—'}</td>
              </tr>
            </tbody>
          </table>
          <p className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500">
            ⓘ {tSub?.earnedNote || 'Earned = agreed price of completed tasks. Not linked to invoices.'}
          </p>
        </div>
      </section>

      {(materialsEquipmentRollup.materialsCount > 0 || materialsEquipmentRollup.equipmentCount > 0) && (
        <section>
          <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            {tSub?.materialsEquipment || 'Materials & equipment on delegated work'}
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg ring-1 ring-gray-900/5 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
            {t(tSub?.rollup, '{m} materials · {e} equipment · client-price side {amount}', {
              m: materialsEquipmentRollup.materialsCount,
              e: materialsEquipmentRollup.equipmentCount,
              amount: formatMoney(materialsEquipmentRollup.priceTotal),
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default OverviewTab;
